import { useCallback, useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";

const cache = new Map();
let queue = null;

const ORIGIN = API.replace(/\/api$/, "");
const fresh = (e) => e && e.exp * 1000 - Date.now() > 90 * 1000;

function flush() {
  const batch = queue;
  queue = null;
  api.post("/files/sign", { paths: [...batch.keys()] })
    .then(({ data }) => {
      batch.forEach((waiters, path) => {
        const u = data.urls[path];
        if (u) cache.set(path, { url: ORIGIN + u, exp: data.expires_at });
        waiters.forEach((w) => (u ? w.resolve(ORIGIN + u) : w.reject(new Error("not allowed"))));
      });
    })
    .catch((e) => batch.forEach((waiters) => waiters.forEach((w) => w.reject(e))));
}

export function signedUrl(path, { force = false } = {}) {
  if (!path) return Promise.resolve(null);
  const hit = cache.get(path);
  if (!force && fresh(hit)) return Promise.resolve(hit.url);
  return new Promise((resolve, reject) => {
    if (!queue) { queue = new Map(); setTimeout(flush, 0); }
    if (!queue.has(path)) queue.set(path, []);
    queue.get(path).push({ resolve, reject });
  });
}

export const inviteFileUrl = (path, invite) => (path ? `${API}/files/${path}?invite=${encodeURIComponent(invite)}` : null);

export function useFileUrl(path, invite) {
  const [url, setUrl] = useState(invite ? inviteFileUrl(path, invite) : null);
  const last = useRef(0);
  useEffect(() => {
    if (invite) { setUrl(inviteFileUrl(path, invite)); return undefined; }
    let live = true;
    setUrl(null);
    signedUrl(path).then((u) => live && setUrl(u)).catch(() => {});
    return () => { live = false; };
  }, [path, invite]);
  const renew = useCallback(async () => {
    if (invite || !path || Date.now() - last.current < 20000) return null;
    last.current = Date.now();
    const u = await signedUrl(path, { force: true }).catch(() => null);
    if (u) setUrl(u);
    return u;
  }, [path, invite]);
  return [url, renew];
}

export function SignedImg({ path, invite, ...rest }) {
  const [url, renew] = useFileUrl(path, invite);
  if (!url) return <span className={rest.className} aria-hidden="true" />;
  return <img src={url} onError={renew} {...rest} />;
}

export function recoverAudio(audio, renew, resume = false) {
  const at = audio.currentTime;
  const wasPlaying = resume || !audio.paused;
  return renew().then((u) => {
    if (!u) return;
    audio.src = u;
    audio.addEventListener("loadedmetadata", () => { audio.currentTime = at; if (wasPlaying) audio.play().catch(() => {}); }, { once: true });
  });
}

export function retrySigned(audio, path, onFail) {
  if (!audio || !path || audio.dataset.retried === path) { onFail?.(); return; }
  audio.dataset.retried = path;
  const at = audio.currentTime;
  signedUrl(path, { force: true }).then((u) => {
    audio.src = u;
    audio.addEventListener("loadedmetadata", () => { audio.currentTime = at; audio.play().then(() => { delete audio.dataset.retried; }).catch(() => onFail?.()); }, { once: true });
  }).catch(() => onFail?.());
}

export function SignedAudio({ path, ...rest }) {
  const [url, renew] = useFileUrl(path);
  const ref = useRef(null);
  if (!url) return null;
  return <audio ref={ref} src={url} onError={() => ref.current && recoverAudio(ref.current, renew)} {...rest} />;
}

export async function openDownload(kind) {
  // the sign step checks the file exists, so errors show as a message instead of a raw page
  const { data } = await api.post("/downloads/sign", { kind });
  window.location.assign(ORIGIN + data.url);
}
